import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { conditionalDevtools } from '@/lib/storeMiddleware';
import { getErrorMessage } from '@/lib/error';
import { updateKeyboardShortcutSettings } from '@/api/settings';
import type { ShortcutAction, KeyBinding } from '@/types/keyboard';
import type { KeyboardShortcutSettings } from '@/types/settings';

interface KeyboardState {
  // User-configured key bindings (overrides defaults)
  customBindings: Record<string, KeyBinding | null>;

  // Disabled shortcuts
  disabledShortcuts: Set<string>;

  // Global enable/disable
  shortcutsEnabled: boolean;

  // Sync status
  syncing: boolean;
  syncError: string | null;

  // Actions
  setBinding: (action: ShortcutAction, binding: KeyBinding | null) => void;
  resetBinding: (action: ShortcutAction) => void;
  resetAllBindings: () => void;
  toggleShortcut: (action: ShortcutAction, enabled: boolean) => void;
  toggleGlobalShortcuts: (enabled: boolean) => void;
  applyServerSettings: (settings: KeyboardShortcutSettings) => void;
  clearSyncError: () => void;
  reset: () => void;
}

interface PersistedKeyboardState {
  customBindings?: Record<string, KeyBinding | null>;
  disabledShortcuts?: string[];
  shortcutsEnabled?: boolean;
}

type NamedSetState<T> = (
  partial: T | Partial<T> | ((state: T) => T | Partial<T>),
  replace?: boolean,
  actionName?: string
) => void;

export const useKeyboardStore = create<KeyboardState>()(
  conditionalDevtools(
    persist(
      (set) => {
        const setWithAction = set as NamedSetState<KeyboardState>;
        return {
          customBindings: {},
          disabledShortcuts: new Set(),
          shortcutsEnabled: true,
          syncing: false,
          syncError: null,

          setBinding: (action, binding) => {
            setWithAction(
              (state: KeyboardState) => ({
                customBindings: { ...state.customBindings, [action]: binding },
              }),
              false,
              'Keyboard:setBinding'
            );
            queueKeyboardPersist();
          },

          resetBinding: (action) => {
            setWithAction(
              (state: KeyboardState) => {
                const nextBindings = { ...state.customBindings };
                delete nextBindings[action];
                return { customBindings: nextBindings };
              },
              false,
              'Keyboard:resetBinding'
            );
            queueKeyboardPersist();
          },

          resetAllBindings: () => {
            setWithAction({ customBindings: {} }, false, 'Keyboard:resetAllBindings');
            queueKeyboardPersist();
          },

          toggleShortcut: (action, enabled) => {
            setWithAction(
              (state: KeyboardState) => {
                const newDisabled = new Set(state.disabledShortcuts);
                if (enabled) {
                  newDisabled.delete(action);
                } else {
                  newDisabled.add(action);
                }
                return { disabledShortcuts: newDisabled };
              },
              false,
              'Keyboard:toggleShortcut'
            );
            queueKeyboardPersist();
          },

          toggleGlobalShortcuts: (enabled) => {
            setWithAction({ shortcutsEnabled: enabled }, false, 'Keyboard:toggleGlobalShortcuts');
            queueKeyboardPersist();
          },

          applyServerSettings: (settings) => {
            // Don't clobber local edits that haven't finished syncing yet
            // (audit §2.17 #6): an in-flight/pending persist means the local
            // store holds the newest changes and is about to become server truth.
            // Overwriting here would revert the user's just-made customization.
            if (keyboardPersistInFlight || keyboardPersistPending) {
              return;
            }
            set({
              customBindings: cloneCustomBindings(settings?.customBindings),
              disabledShortcuts: new Set(settings?.disabledShortcuts ?? []),
              shortcutsEnabled: settings?.shortcutsEnabled ?? true,
              syncError: null,
            });
          },

          clearSyncError: () => set({ syncError: null }),

          reset: () =>
            set({
              customBindings: {},
              disabledShortcuts: new Set(),
              shortcutsEnabled: true,
              syncing: false,
              syncError: null,
            }),
        };
      },
      {
        name: 'keyboard-shortcuts-storage',
        // Custom serializer for Set
        partialize: (state) => ({
          customBindings: state.customBindings,
          disabledShortcuts: Array.from(state.disabledShortcuts),
          shortcutsEnabled: state.shortcutsEnabled,
        }),
        merge: (persistedState, currentState) => {
          const incoming = (persistedState as PersistedKeyboardState | undefined) ?? {};
          return {
            ...currentState,
            ...incoming,
            disabledShortcuts: new Set(incoming.disabledShortcuts ?? []),
          };
        },
      }
    ),
    { name: 'KeyboardStore' }
  )
);

function cloneCustomBindings(bindings?: Record<string, KeyBinding | null> | null) {
  if (!bindings || typeof bindings !== 'object') {
    return {};
  }
  return Object.entries(bindings).reduce<Record<string, KeyBinding | null>>((acc, [action, binding]) => {
    if (binding === null) {
      acc[action] = null;
      return acc;
    }
    acc[action] = { ...binding };
    return acc;
  }, {});
}

function serializeKeyboardState(state: KeyboardState): KeyboardShortcutSettings {
  return {
    shortcutsEnabled: state.shortcutsEnabled,
    customBindings: cloneCustomBindings(state.customBindings),
    disabledShortcuts: Array.from(state.disabledShortcuts),
  };
}

let keyboardPersistInFlight = false;
let keyboardPersistPending = false;

function queueKeyboardPersist() {
  if (keyboardPersistInFlight) {
    keyboardPersistPending = true;
    return;
  }

  keyboardPersistInFlight = true;
  keyboardPersistPending = false;

  void (async () => {
    do {
      keyboardPersistPending = false;
      const payload = serializeKeyboardState(useKeyboardStore.getState());
      useKeyboardStore.setState({ syncing: true, syncError: null });
      try {
        await updateKeyboardShortcutSettings(payload);
        useKeyboardStore.setState({ syncing: false });
      } catch (error) {
        console.error('Failed to persist keyboard shortcuts', error);
        useKeyboardStore.setState({
          syncing: false,
          syncError: getErrorMessage(error) ?? 'Failed to save keyboard shortcut changes',
        });
      }
    } while (keyboardPersistPending);

    keyboardPersistInFlight = false;
  })();
}
